import { api } from '@/api';
import type { TrackItMeasurementFilterForCombinationDto } from '@/api/generator';
import { mapBackendErrorToAjvErrors } from '@/validation/validators/backendValidator';
import { ref, type Ref } from 'vue';

export interface TrackItFilterValidationEntry {
  // Identifies the dialog element the filter came from, so an error can be shown there.
  key: string;
  filter: TrackItMeasurementFilterForCombinationDto;
}

// The backend reports errors per list member, e.g. `filters[2]`.
function indexOfMemberPath(path: string): number | undefined {
  const match = /\[(\d+)\]/.exec(path);
  return match ? Number(match[1]) : undefined;
}

// Validates on demand (focus loss, Apply) rather than on every change, so typing doesn't fire a
// request per keystroke.
export function useTrackItFilterValidation(entries: Ref<TrackItFilterValidationEntry[]>) {
  const errorsByKey = ref(new Map<string, string>());
  const validating = ref(false);

  let latestRequestId = 0;

  // Resolves to whether the current entries passed; a superseded call resolves to false.
  async function validate(): Promise<boolean> {
    const requestId = ++latestRequestId;
    const validatedEntries = entries.value;
    const nextErrors = new Map<string, string>();
    validating.value = true;

    const { success } = await api.trackItImportApi.validateTrackItMeasurementParameterFilters(
      { trackItMeasurementFilterForCombinationDto: validatedEntries.map(({ filter }) => filter) },
      [
        (error) => {
          const mappedErrors = mapBackendErrorToAjvErrors(error);
          if (!mappedErrors?.length) return error;

          for (const { path, errors } of mappedErrors) {
            const index = indexOfMemberPath(path);
            const entry = index === undefined ? undefined : validatedEntries[index];
            if (entry && !nextErrors.has(entry.key)) {
              nextErrors.set(entry.key, errors[0]);
            }
          }
          return undefined;
        },
      ],
    );

    if (requestId !== latestRequestId) return false;

    errorsByKey.value = nextErrors;
    validating.value = false;
    return success;
  }

  function reset() {
    latestRequestId++;
    errorsByKey.value = new Map();
    validating.value = false;
  }

  return { errorsByKey, validating, validate, reset };
}
